import { useCallback, useEffect, useState } from "react";
import { Tag, History, FileText, Search, ArrowUp, ArrowDown } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import api, { formatApiError, fileUrl } from "../lib/api";

const money = (v) => (v == null ? "—" : `₹${Number(v).toLocaleString("en-IN")}`);

const Pricing = () => {
  const [tab, setTab] = useState("master");
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [history, setHistory] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/pricing/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/pricing/history", { params: { limit: 200 } }).then((r) => setHistory(r.data)).catch(() => {});
  }, []);

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/catalog", { params: { search: search || undefined, limit: 200 } });
      setRows(r.data.items.filter((i) => i.rate != null || i.mrp != null));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(loadCatalog, 250);
    return () => clearTimeout(t);
  }, [loadCatalog]);

  const sellPrice = (it) =>
    it.selling_price != null
      ? it.selling_price
      : it.mrp != null && it.discount_pct != null
        ? Math.round(it.mrp * (1 - it.discount_pct / 100) * 100) / 100
        : it.mrp;

  const marginPct = (it) => {
    const sell = sellPrice(it);
    if (!sell || !it.rate) return null;
    return Math.round(((sell - it.rate) / sell) * 1000) / 10;
  };

  return (
    <div data-testid="pricing-page" className="space-y-5">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Pricing Engine</h1>
        <p className="text-base text-slate-600 dark:text-slate-400">
          Cost rate → MRP → discount → selling price → margin. Every change carries its source document as proof.
        </p>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="pricing-summary-bar">
          {[
            ["Priced articles", summary.priced_articles, "text-slate-900 dark:text-slate-100", "stat-priced-articles"],
            ["Price changes (7d)", summary.changes_7d, "text-amber-600 dark:text-amber-400", "stat-changes-7d"],
            ["Brands", summary.brands, "text-cyan-600 dark:text-cyan-400", "stat-pricing-brands"],
            ["Catalog total", summary.total_articles, "text-slate-900 dark:text-slate-100", "stat-pricing-total"],
          ].map(([label, value, tone, testid]) => (
            <div key={testid} data-testid={testid} className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
              <div className="text-sm font-mono uppercase tracking-widest text-slate-600 dark:text-slate-400">{label}</div>
              <div className={`mt-1 text-2xl md:text-3xl font-extrabold font-mono ${tone}`}>{value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2" data-testid="pricing-tabs">
        <button
          data-testid="pricing-tab-master"
          onClick={() => setTab("master")}
          className={`inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-base font-semibold transition-colors ${
            tab === "master" ? "bg-emerald-500 text-emerald-950" : "border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300"
          }`}
        >
          <Tag className="h-4 w-4" /> Price master
        </button>
        <button
          data-testid="pricing-tab-changelog"
          onClick={() => setTab("changelog")}
          className={`inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-base font-semibold transition-colors ${
            tab === "changelog" ? "bg-emerald-500 text-emerald-950" : "border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300"
          }`}
        >
          <History className="h-4 w-4" /> Change log ({history.length})
        </button>
      </div>

      {tab === "master" ? (
        <>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-500" />
            <input
              data-testid="pricing-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search article no or name…"
              className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 pl-11 pr-3 py-3 text-base text-slate-900 dark:text-slate-100 focus:border-emerald-500 focus:outline-none"
            />
          </div>
          <div className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-x-auto" data-testid="pricing-master-wrap">
            {loading ? (
              <div className="flex h-40 items-center justify-center"><div className="h-8 w-8 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" /></div>
            ) : rows.length === 0 ? (
              <p className="p-10 text-center text-base text-slate-600 dark:text-slate-400" data-testid="pricing-empty">
                No priced articles yet. Upload a price-list PDF on the Capture page and hit "Apply prices".
              </p>
            ) : (
              <table className="w-full text-base" data-testid="pricing-master-table">
                <thead>
                  <tr className="border-b border-slate-300 dark:border-slate-700 text-left text-sm font-mono uppercase tracking-widest text-slate-600 dark:text-slate-400">
                    <th className="px-4 py-3">Article</th>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Brand</th>
                    <th className="px-4 py-3 text-right">Cost rate</th>
                    <th className="px-4 py-3 text-right">MRP</th>
                    <th className="px-4 py-3 text-right">Disc%</th>
                    <th className="px-4 py-3 text-right">Selling</th>
                    <th className="px-4 py-3 text-right">Margin%</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((it) => {
                    const m = marginPct(it);
                    return (
                      <tr key={`${it.article_no}-${it.brand}`} data-testid={`pricing-row-${it.article_no}`} className="border-b border-slate-200 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                        <td className="px-4 py-2.5 font-mono text-cyan-600 dark:text-cyan-400">{it.article_no}</td>
                        <td className="px-4 py-2.5 text-slate-700 dark:text-slate-300">{it.name || "—"}</td>
                        <td className="px-4 py-2.5 text-slate-600 dark:text-slate-400">{it.brand}</td>
                        <td className="px-4 py-2.5 text-right font-mono text-slate-800 dark:text-slate-200">{money(it.rate)}</td>
                        <td className="px-4 py-2.5 text-right font-mono text-slate-600 dark:text-slate-400">{money(it.mrp)}</td>
                        <td className="px-4 py-2.5 text-right font-mono text-amber-600 dark:text-amber-400">{it.discount_pct != null ? `${it.discount_pct}%` : "—"}</td>
                        <td className="px-4 py-2.5 text-right font-mono text-emerald-600 dark:text-emerald-400">{money(sellPrice(it))}</td>
                        <td className={`px-4 py-2.5 text-right font-mono font-bold ${m != null && m < 15 ? "text-rose-600 dark:text-rose-400" : "text-slate-800 dark:text-slate-200"}`}>
                          {m != null ? `${m}%` : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : (
        <div className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-x-auto" data-testid="pricing-changelog-wrap">
          {history.length === 0 ? (
            <p className="p-10 text-center text-base text-slate-600 dark:text-slate-400" data-testid="changelog-empty">No price changes logged yet.</p>
          ) : (
            <table className="w-full text-base" data-testid="pricing-changelog-table">
              <thead>
                <tr className="border-b border-slate-300 dark:border-slate-700 text-left text-sm font-mono uppercase tracking-widest text-slate-600 dark:text-slate-400">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Brand</th>
                  <th className="px-4 py-3">Article</th>
                  <th className="px-4 py-3 text-right">Old → New</th>
                  <th className="px-4 py-3 text-right">Change</th>
                  <th className="px-4 py-3">Source</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h, i) => {
                  const delta = h.old_rate != null && h.new_rate != null && h.old_rate !== 0
                    ? Math.round(((h.new_rate - h.old_rate) / h.old_rate) * 1000) / 10
                    : null;
                  const up = delta != null && delta > 0;
                  return (
                    <tr key={h.id || i} data-testid={`changelog-row-${i}`} className="border-b border-slate-200 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                      <td className="px-4 py-2.5 font-mono text-slate-600 dark:text-slate-400">{format(new Date(h.created_at), "d MMM yy")}</td>
                      <td className="px-4 py-2.5 text-slate-700 dark:text-slate-300">{h.brand}</td>
                      <td className="px-4 py-2.5 font-mono text-cyan-600 dark:text-cyan-400">{h.article_no}{h.size ? ` · ${h.size}` : ""}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-slate-800 dark:text-slate-200">
                        {h.old_rate != null ? money(h.old_rate) : "new"} → <span className="font-bold">{money(h.new_rate)}</span>
                      </td>
                      <td className={`px-4 py-2.5 text-right font-mono font-bold ${up ? "text-rose-600 dark:text-rose-400" : "text-emerald-600 dark:text-emerald-400"}`}>
                        {delta != null ? (
                          <span className="inline-flex items-center gap-0.5 justify-end">
                            {up ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />}{Math.abs(delta)}%
                          </span>
                        ) : "—"}
                      </td>
                      <td className="px-4 py-2.5">
                        {h.source_path ? (
                          <a data-testid={`changelog-source-${i}`} href={fileUrl(h.source_path)} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-indigo-600 dark:text-indigo-400 hover:underline">
                            <FileText className="h-4 w-4" /> PDF
                          </a>
                        ) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default Pricing;
